// An extension's parts around a streamed Mac window (docs/features/extensions.md: "With a stream", Contract › Docs
// `stream:…`): when a window of an app an extension controls is streamed, its `stream:<windowId>` doc (or its
// `stream:<bundleId>` one — the window's own wins) puts floating ornaments above, below, leading and trailing the
// picture, e.g. Chrome's tabs as buttons over the Chrome stream. The window's ⋯ gets the doc's `menu` actions and the
// view: **Stream** (this) or **Behind the Scenes** (the extension's own pane, from Home). Buttons answer the extension
// with `action`; a keyRow here sends its keys into this stream's input — only what the person pressed. The action
// `sheet` (a button's, a ⋯ item's id) never goes to the Mac: it opens the window's Shortcuts sheet (shortcuts/).
//
// One seam, used by macwindow.js: `new StreamParts(win)` → `above()`, `below()`, `leading()`, `trailing()`,
// `menuButton()`, `overlay()` (HTML); `act(kind, arg, el, e)` (undefined when a tap isn't its); `dispose()`.
import { esc, icon, menu } from '../../core/kit.js'
import { Extensions, runAction, keyEvent } from '../../extensions/extensions.js'
import { resetParts, partHTML, partsAct, partsTyped, confirmHTML, partsReady } from '../display/parts.js'

const img = (symbol, colour = 'var(--c-ink)') => `<img class="sym" src="${icon(symbol, colour)}">`

export class StreamParts {
  constructor(win) {
    this.win = win
    this.pss = new Map()      // ext → its parts' drawing state
    this.off = Extensions.on(() => win.render())
    partsReady.then(() => win.render())
  }
  get ctx() { return this.win.ctx }
  get macKey() { return this.ctx.macID || this.ctx.macKey || null }
  get window() { return this.win.stream?.window || null }
  get bundle() { const w = this.window; return w?.bundleID || w?.bundleId || '' }
  get slots() { return Extensions.streamOf(this.macKey, this.window) }

  ps(ext) {
    let ps = this.pss.get(ext)
    if (!ps) { ps = { env: { macKey: this.macKey, ext } }; this.pss.set(ext, ps) }
    return ps
  }

  /// Start of a drawing: every extension's lookups start again.
  begin() { for (const ps of this.pss.values()) resetParts(ps); this.cached = this.slots; return this.cached }

  /// One slot's parts, each extension's in its own glass, tagged with the extension so its taps find their state.
  group(items, cls) {
    if (!items?.length) return ''
    const byExt = new Map()
    for (const it of items) { if (!byExt.has(it.ext)) byExt.set(it.ext, []); byExt.get(it.ext).push(it.part) }
    return [...byExt.entries()].map(([ext, parts]) => {
      const ps = this.ps(ext)
      if (!ps.nodes) resetParts(ps)
      return `<div class="xs-orn ${cls}" data-xsext="${esc(ext)}">${parts.map((p, i) => partHTML(p, ps, `${cls}${i}`)).join('')}</div>`
    }).join('')
  }
  above() { const s = this.begin(); return s?.above?.length ? `<div class="xs-band xs-part">${this.group(s.above, 'a')}</div>` : '' }
  below() { const s = this.cached; return s?.below?.length ? `<div class="xs-band xs-part">${this.group(s.below, 'b')}</div>` : '' }
  leading() { const s = this.cached; return s?.leading?.length ? `<div class="xs-side xs-part">${this.group(s.leading, 'l')}</div>` : '' }
  trailing() { const s = this.cached; return s?.trailing?.length ? `<div class="xs-side xs-part">${this.group(s.trailing, 't')}</div>` : '' }
  sides() { const s = this.cached; return !!(s?.leading?.length || s?.trailing?.length) }

  /// The ⋯ in the ornament: only for a window an extension controls or decorates.
  menuButton() { return this.cached ? `<button class="ob icon${this.win.menu === 'xs' ? ' open' : ''}" data-act="menu:xs" aria-label="Actions" data-help="The extension’s actions for this window, and how to see it: streamed, or behind the scenes in its own pane">${img('ellipsis')}</button>` : '' }

  overlay() {
    const s = this.cached
    let out = ''
    if (this.win.menu === 'xs' && s) {
      const rows = s.menu.map((m, i) => ({ act: 'xsmenu:' + i, label: m.action.title || m.action.id, symbol: m.action.symbol }))
      if (rows.length) rows.push('-')
      for (const src of s.sources) {
        const view = Extensions.viewOf(this.macKey, src.ext, { app: this.bundle })
        rows.push({ section: s.sources.length > 1 ? `View · ${src.name}` : 'View' },
          { act: `xsview:stream|${src.ext}`, label: 'Stream', checked: view === 'stream', symbol: 'macwindow.on.rectangle' },
          { act: `xsview:behindTheScenes|${src.ext}`, label: 'Behind the Scenes', checked: view !== 'stream', symbol: 'sparkles' })
      }
      out += menu(rows, { right: 12, top: 56 })
    }
    for (const ps of this.pss.values()) out += confirmHTML(ps)
    return out
  }

  handler(ext) {
    const ctx = this.ctx, target = this.cached?.sources.find((x) => x.ext === ext)?.target || (this.bundle ? 'stream:' + this.bundle : null)
    return {
      // `sheet` is the client's own: the Shortcuts sheet at `args.section`, the search focused — nothing goes to the Mac
      action: (a) => a.action === 'sheet' ? this.win.sc?.show({ section: a.args?.section ?? null, focus: true }) : runAction({ client: ctx.client, open: (k, arg) => ctx.open?.(k, arg), openMacWindow: (w) => ctx.openMacWindow?.(w), note: (t) => ctx.note?.(t) }, this.macKey, { ext, action: a.action, args: a.args, target }),
      key: (to, spec) => {
        if (to !== 'stream') { ctx.note?.('Keys for a terminal don’t go to a streamed window.'); return }
        const ev = keyEvent(spec), s = this.win.stream
        if (!ev || !s?.send) return
        s.touched?.()
        s.send({ action: 'key', usage: ev.usage, pressed: true, modifiers: ev.modifiers })
        s.send({ action: 'key', usage: ev.usage, pressed: false, modifiers: ev.modifiers })
      },
      type: () => ctx.note?.('This window has no terminal to type into.'),
      openURL: (url, stream) => {
        if (!stream) { ctx.openURL?.(url); return }
        ctx.macWindows?.openURL?.(url, true).then((r) => { if (r?.window) ctx.openMacWindow?.(r.window); else if (r?.error) ctx.note?.(r.error) }, () => ctx.note?.('The Mac didn’t answer.'))
      },
      openLink: (url) => ctx.openURL?.(url),
      note: (t) => ctx.note?.(t),
    }
  }

  /// A tap: the ⋯'s items, else the parts' own (their extension found from the glass they are in).
  act(kind, arg, el, e) {
    const s = this.cached
    if (kind === 'xsmenu') {
      const m = s?.menu?.[+arg]
      if (!m) return false
      if (m.action.id === 'sheet') { this.win.sc?.show({ section: m.action.args?.section ?? null, focus: true }); return true }
      if (m.action.confirm) { this.ps(m.ext).confirm = { action: m.action.id, args: m.action.args, confirm: m.action.confirm, title: m.action.title }; return }
      this.handler(m.ext).action({ action: m.action.id, args: m.action.args })
      return false
    }
    if (kind === 'xsview') {
      const [view, ext] = arg.split('|')
      Extensions.setView(this.macKey, { ext, app: this.bundle, view })
      if (view === 'behindTheScenes') this.ctx.note?.('Behind the Scenes: its sessions open in Cody’s own pane — Home lists them.')
      return
    }
    const ext = el?.closest?.('[data-xsext]')?.dataset.xsext || (kind === 'xp' && arg.startsWith('confirm') ? [...this.pss.entries()].find(([, p]) => p.confirm)?.[0] : null)
    if (!ext) return undefined
    const r = partsAct(this.ps(ext), kind, arg, el, e, this.handler(ext))
    return r
  }
  typed(key, value) { for (const ps of this.pss.values()) if (partsTyped(ps, key, value)) return true; return false }

  dispose() { this.off?.() }
}
